import React from 'react';

import {useFormatNumber, I18nApi, LocalizedHtml} from '@udemy/i18n';
import {type IconName} from '@udemy/icons';
import {TextSkeleton} from '@udemy/react-reveal-components';

import {TopicPpMerchandisingModuleProps} from './topic-pp-merchandising-module.react-component';

export const calculateBenefits = (
    props: TopicPpMerchandisingModuleProps,
    courseCount: number,
    isCollectionStatsLoading: boolean,
    i18n: I18nApi,
    formatNumber: ReturnType<typeof useFormatNumber>['formatNumber'],
): {icon: IconName; text: React.ReactNode}[] => {
    const {gettext, interpolate} = i18n;

    if (props.variant === 'cert') {
        return [
            {
                icon: 'video-group' as const,
                text: interpolate(
                    gettext(
                        'Choose from %(numPrepCourses)s+ top-rated prep courses and %(numPracticeExams)s full-length practice exams',
                    ),
                    {
                        numPrepCourses: formatNumber(props.numPrepCourses),
                        numPracticeExams: formatNumber(props.numPracticeExams),
                    },
                    true,
                ),
            },
            {
                icon: 'sparkles' as const,
                text: gettext(
                    'Instant answers when you need them - from instructors, the community, and our AI Assistant trained on course content',
                ),
            },
            props.offersRetakeInsurance
                ? {
                      icon: 'coupon' as const,
                      text: gettext(
                          'Save on exam vouchers with retake assurance for a second attempt',
                      ),
                  }
                : undefined,
            {
                icon: 'certificate' as const,
                text: (
                    <LocalizedHtml
                        html={interpolate(
                            gettext(
                                'Continue your certification journey with prep for other <a class="examAggregatorUrl">%(examProvider)s exams</a> and <a class="aggregatorUrl">industry-leading providers</a>',
                            ),
                            {
                                examProvider: props.examProvider,
                            },
                            true,
                        )}
                        interpolate={{
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            examAggregatorUrl: <a href={props.examAggregatorUrl} />,
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            aggregatorUrl: <a href={'/browse/certification/'} />,
                        }}
                    />
                ),
            },
            {
                icon: 'list-alt' as const,
                text: gettext(
                    'Build your career with additional content on professional development and interview prep',
                ),
            },
        ].filter((e) => e !== undefined);
    } else {
        return [
            {
                icon: 'play' as const,
                text: isCollectionStatsLoading ? (
                    <TextSkeleton lineCountPerParagraph={1} paragraphCount={1} />
                ) : (
                    interpolate(
                        gettext(
                            '%(courses)s+ top-rated courses including AI, web development, data science, and IT',
                        ),
                        {courses: formatNumber(courseCount)},
                        true,
                    )
                ),
            },
            {
                icon: 'sparkles' as const,
                text: gettext(
                    'AI-powered soft skills practice through interactive role play scenarios',
                ),
            },
            {
                icon: 'code' as const,
                text: gettext('Hands-on technical practice through labs and coding exercises'),
            },
            {
                icon: 'gift' as const,
                text: gettext(
                    'Comprehensive certification prep for AWS, Microsoft, CompTIA, PMI, and more',
                ),
            },
            {
                icon: 'info-outline' as const,
                text: gettext('Ad-free'),
            },
        ];
    }
};
